import styles from "./ConfirmDetailsPanel.module.scss";

import {
  ChangeEvent,
  KeyboardEvent,
  useCallback,
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
} from "react";

import { formatInTimeZone } from "date-fns-tz";
import { Trans, useTranslation } from "react-i18next";

import { AppButton } from "@/modules/shared/components/AppButton";
import { AppInput } from "@/modules/shared/components/AppInput";
import { IconButton } from "@/modules/shared/components/IconButton";
import { EXTERNAL_LINKS } from "@/modules/shared/consts/externalLinks";
import {
  BOOKING_EVENTS,
  RESCHEDULE_EVENTS,
} from "@/modules/tracking/consts/Events";
import { trackBookingEvent } from "@/modules/tracking/utils/trackBookingEvent";

import {
  BookingKinds,
  CreateSessionErrorReasons,
  CreateSessionStatuses,
  useBookingContext,
} from "../../context/BookingContext";
import ArrowLeft from "../../svgs/arrow-left.svg";
import CalendarFrameSvg from "../../svgs/calendar-frame.svg";
import { buildBookingEventProps } from "../../utils/buildBookingEventProps";
import { NeedHelpLink } from "../NeedHelpLink";
import { UnrecognizedEmailAppPrompt } from "../UnrecognizedEmailAppPrompt";

type ConfirmDetailsPanelProps = {
  onSchedule: ({ name, email }: { name: string; email: string }) => void;
  supportRoute?: string;
};

type FieldError = Record<string, "required" | "invalid">;

export function ConfirmDetailsPanel({
  onSchedule,
  supportRoute,
}: ConfirmDetailsPanelProps) {
  const { t } = useTranslation();

  const {
    selectedDateTime,
    timezone,
    clearSelectedDateTime,
    createSessionStatus,
    createSessionErrorReason,
    clearCreateSessionErrorReason,
    memberName,
    memberEmail,
    isKnownMember,
    isPrefillEditable,
    contactDraft,
    setContactDraft,
    coachId,
    isRescheduling,
    rescheduleSessionId,
    originalSessionStart,
    bookingKind,
    serviceName,
    preSessionNotesEnabled,
    setNotesStepDone,
  } = useBookingContext();

  const isCoachMatchingServiceBooking =
    bookingKind === BookingKinds.COACH_MATCHING_SERVICE;
  // The in-app embeds always arrive identified, so only public pages land here.
  const isPublicBookingFlow = !isKnownMember;

  const eventProps = useMemo(
    () =>
      buildBookingEventProps({
        isRescheduling,
        isCoachMatchingServiceBooking,
        coachId,
        serviceName,
        rescheduleSessionId,
      }),
    [
      isRescheduling,
      isCoachMatchingServiceBooking,
      coachId,
      serviceName,
      rescheduleSessionId,
    ],
  );

  const emailInputRef = useRef<HTMLInputElement | null>(null);
  const emailInputId = useId();
  const emailHelperTextId = `${emailInputId}-helper`;
  const [hasUserEditedName, setHasUserEditedName] = useState(false);
  const [hasUserEditedEmail, setHasUserEditedEmail] = useState(false);
  const [errors, setErrors] = useState<FieldError>({});

  // Until the user edits a field it shows the (possibly late-arriving) prefill
  // from context; once edited, their input is never clobbered by a prefill.
  const nameValue = contactDraft.name ?? memberName ?? "";
  const emailValue = contactDraft.email ?? memberEmail ?? "";

  const isNameLocked =
    isKnownMember && Boolean(memberName) && !isPrefillEditable;
  const isEmailLocked =
    isKnownMember && Boolean(memberEmail) && !isPrefillEditable;
  const hasUnrecognizedEmailError =
    createSessionErrorReason === CreateSessionErrorReasons.UNRECOGNIZED_EMAIL;
  const hasRateLimitError =
    createSessionErrorReason === CreateSessionErrorReasons.RATE_LIMITED;
  const showAppPrompt =
    hasUnrecognizedEmailError && isCoachMatchingServiceBooking;
  const unrecognizedEmailErrorKey = isCoachMatchingServiceBooking
    ? "book.confirmDetails.coachMatching.errors.unrecognizedEmail"
    : "book.confirmDetails.errors.unrecognizedEmail";

  useEffect(() => {
    if (hasUnrecognizedEmailError) {
      emailInputRef.current?.focus();
    }
  }, [hasUnrecognizedEmailError]);

  const dateTimeLabel = useMemo(() => {
    if (!selectedDateTime || !timezone) {
      return "";
    }

    const date = new Date(selectedDateTime);
    const datePart = formatInTimeZone(date, timezone.value, "EEEE, MMMM d");
    const timePart = formatInTimeZone(
      date,
      timezone.value,
      "h:mma",
    ).toLowerCase();
    const tzAbbreviation = formatInTimeZone(date, timezone.value, "zzz");

    return `${datePart} at ${timePart} (${tzAbbreviation})`;
  }, [selectedDateTime, timezone]);

  const validateInputs = (name: string, email: string) => {
    const errors: FieldError = {};

    if (!name) {
      errors.name = "required";
    }

    const isEmailValid = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);

    if (!email) {
      errors.email = "required";
    } else if (!isEmailValid) {
      errors.email = "invalid";
    }

    return errors;
  };

  const handleOnCTAClick = useCallback(() => {
    const trimmedName = nameValue.trim();
    const trimmedEmail = emailValue.trim();

    const nextErrors = validateInputs(trimmedName, trimmedEmail);

    setErrors(nextErrors);

    if (Object.keys(nextErrors).length > 0) {
      return;
    }

    if (isRescheduling) {
      trackBookingEvent(RESCHEDULE_EVENTS.RESCHEDULE_SUBMITTED, {
        ...eventProps,
        original_session_start_time: originalSessionStart ?? null,
        session_start_time: selectedDateTime ?? null,
      });
    } else {
      trackBookingEvent(BOOKING_EVENTS.BOOKING_SUBMITTED, eventProps);
    }
    onSchedule({ name: trimmedName, email: trimmedEmail });
  }, [
    nameValue,
    emailValue,
    onSchedule,
    eventProps,
    isRescheduling,
    originalSessionStart,
    selectedDateTime,
  ]);

  // Back returns to the notes step when it's in play; otherwise clears the time.
  const handleBack = useCallback(() => {
    clearCreateSessionErrorReason();
    if (preSessionNotesEnabled && !isRescheduling) {
      setNotesStepDone(false);
      return;
    }
    clearSelectedDateTime();
  }, [
    preSessionNotesEnabled,
    isRescheduling,
    setNotesStepDone,
    clearSelectedDateTime,
    clearCreateSessionErrorReason,
  ]);

  const handleOnNameKeyDown = useCallback(
    (e: KeyboardEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      if (e.key === "Enter") {
        e.preventDefault();
        emailInputRef.current?.focus();
      }
    },
    [],
  );

  const trackFieldEdited = useCallback(
    (fieldName: "name" | "email") => {
      const eventName = isRescheduling
        ? RESCHEDULE_EVENTS.CONFIRMATION_FIELD_EDITED
        : BOOKING_EVENTS.CONFIRMATION_FIELD_EDITED;

      trackBookingEvent(eventName, { ...eventProps, field_name: fieldName });
    },
    [eventProps, isRescheduling],
  );

  const handleOnNameChange = useCallback(
    (e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      setContactDraft({ name: e.target.value });
      if (!hasUserEditedName) {
        trackFieldEdited("name");
        setHasUserEditedName(true);
      }
    },
    [hasUserEditedName, trackFieldEdited, setContactDraft],
  );

  const handleOnEmailChange = useCallback(
    (e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      setContactDraft({ email: e.target.value });
      if (hasUnrecognizedEmailError) {
        clearCreateSessionErrorReason();
      }
      if (!hasUserEditedEmail) {
        trackFieldEdited("email");
        setHasUserEditedEmail(true);
      }
    },
    [
      clearCreateSessionErrorReason,
      hasUnrecognizedEmailError,
      hasUserEditedEmail,
      trackFieldEdited,
      setContactDraft,
    ],
  );

  return (
    <div className={styles.container}>
      <IconButton
        icon={ArrowLeft}
        aria-label={t("book.confirmDetails.back")}
        className={styles.backIcon}
        onClick={handleBack}
      />
      <div className={styles.headerContainer}>
        <span className={styles.title}>{t("book.confirmDetails.title")}</span>
        <div className={styles.subtitleContainer}>
          <img src={CalendarFrameSvg} alt="CalendarFrame" />
          <span className={styles.subtitle}>{dateTimeLabel}</span>
        </div>
        <span className={styles.contactPreferencesCopy}>
          {t(
            isPublicBookingFlow
              ? "book.confirmDetails.publicFlow.contactPreferencesCopy"
              : "book.confirmDetails.contactPreferencesCopy",
          )}
        </span>
      </div>
      <div className={styles.bodyContainer}>
        <AppInput
          className={styles.appInput}
          label={t(
            isPublicBookingFlow
              ? "book.confirmDetails.publicFlow.inputs.name.label"
              : "book.confirmDetails.inputs.name.label",
          )}
          placeholder={t("book.confirmDetails.inputs.name.placeholder")}
          autoCapitalize="words"
          value={nameValue}
          onChange={handleOnNameChange}
          onKeyDown={handleOnNameKeyDown}
          disabled={isNameLocked}
          error={
            errors.name
              ? t("book.confirmDetails.inputs.name.errors.required")
              : undefined
          }
        />
        <div className={styles.emailField}>
          {isPublicBookingFlow && (
            <>
              <label className={styles.emailLabel} htmlFor={emailInputId}>
                {t("book.confirmDetails.publicFlow.inputs.email.label")}
              </label>
              <span id={emailHelperTextId} className={styles.emailHelperText}>
                {t("book.confirmDetails.publicFlow.inputs.email.helperText")}
              </span>
            </>
          )}
          <AppInput
            id={emailInputId}
            className={styles.appInput}
            label={
              isPublicBookingFlow
                ? undefined
                : t("book.confirmDetails.inputs.email.label")
            }
            placeholder={t("book.confirmDetails.inputs.email.placeholder")}
            autoCapitalize="off"
            inputRef={emailInputRef}
            value={emailValue}
            onChange={handleOnEmailChange}
            disabled={isEmailLocked}
            describedBy={isPublicBookingFlow ? emailHelperTextId : undefined}
            invalid={Boolean(errors.email) || hasUnrecognizedEmailError}
            error={
              errors.email
                ? t(`book.confirmDetails.inputs.email.errors.${errors.email}`)
                : hasUnrecognizedEmailError
                  ? t(unrecognizedEmailErrorKey)
                  : undefined
            }
          />
        </div>
        {showAppPrompt && (
          <UnrecognizedEmailAppPrompt supportRoute={supportRoute} />
        )}
        {hasRateLimitError && (
          <p role="alert" className={styles.formError}>
            {t("book.confirmDetails.errors.rateLimited")}
          </p>
        )}
        <span className={styles.noticeLabel}>
          <Trans
            i18nKey={"book.confirmDetails.noticeLabel"}
            components={{
              terms: (
                <a href={EXTERNAL_LINKS.TERMS_OF_SERVICE} target="_blank" />
              ),
              policy: (
                <a href={EXTERNAL_LINKS.PRIVACY_POLICY} target="_blank" />
              ),
            }}
          />
        </span>
        {!showAppPrompt && (
          <div className={styles.needHelpContainer}>
            <NeedHelpLink mobileLink to={supportRoute} />
          </div>
        )}
      </div>
      <AppButton
        variant="critical"
        className={styles.cta}
        onClick={handleOnCTAClick}
        isLoading={createSessionStatus === CreateSessionStatuses.LOADING}
      >
        {t("book.confirmDetails.cta")}
      </AppButton>
    </div>
  );
}
